<template>
  <asc-card
    class="asc-doc-mgmt-content-editor--card"
    flat
    :tile="true"
    :no-spacing="true"
    :outlined="true"
  >
    <div class="editor">
      <div class="editor--header">
        <v-row no-gutters>
          <v-col class="editor-title" cols="6">{{ getContentTitle }}</v-col>
          <v-col class="editor-action d-flex justify-end" cols="6">
            <div v-if="!isEditable">
              <asc-accent-button
                button-text="Edit"
                outline
                secondary
                :on-click="handleEdit"
              ></asc-accent-button>
            </div>
            <div v-if="isEditable" class="d-flex justify-end">
              <asc-accent-button
                button-text="Cancel"
                outline
                secondary
                :disabled="isCancelDisabled"
                :on-click="handleCancel"
              ></asc-accent-button>
              <asc-accent-button
                button-text="Save"
                :loading="content.saving"
                :disabled="isSaveDisabled"
                :on-click="handleSave"
              ></asc-accent-button>
            </div>
          </v-col>
        </v-row>
      </div>
      <div class="editor--content">
        <asc-rich-text-input
          :comment="updatedContent"
          :can-edit="isEditable"
          :max-length="max"
          :menu-bar="isEditable"
          :placeholder="isEditable ? 'Say something...' : ''"
          hide-details
          @updated="handleUpdatedText"
        >
        </asc-rich-text-input>
      </div>
      <div
        v-if="content && (content.lastUpdatedOn || content.user.userId)"
        class="editor--footer"
      >
        Last Edit: {{ getLastEditedDate }} by {{ content.user.firstName }}
        {{ content.user.lastName }}
      </div>
    </div>
  </asc-card>
</template>

<script>
import AscRichTextInput from '@/components/atoms/AscRichTextInput'
import AscAccentButton from '@/components/atoms/AscAccentButton'
import AscCard from '@/components/atoms/AscCard'

import { getDateFormatted } from '@/utilities/dates'

export default {
  name: 'AscDocMgmtContentEditor',

  components: {
    AscRichTextInput,
    AscCard,
    AscAccentButton,
  },

  props: {
    content: {
      type: Object,
      default: () => {},
    },
    user: {
      type: Object,
      default: () => {},
    },
    max: {
      type: Number,
      default: null,
    },
    emptyComment: {
      type: String,
      default: '<p></p>',
    },
    readonly: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      updatedContent: this.content.content,
      disableSave: true,
      editable: this.readonly,
    }
  },

  computed: {
    getContentTitle() {
      return this.content.name
    },
    isEditable() {
      return !this.editable
    },
    getLastEditedDate() {
      return this.content.lastUpdatedOn
        ? getDateFormatted(this.content.lastUpdatedOn, 'MMM/DD/YYYY')
        : ''
    },
    isSaveDisabled() {
      return (
        this.content.saving ||
        this.disableSave ||
        this.content.content === this.updatedContent
      )
    },
    isCancelDisabled() {
      return this.content.saving
    },
  },

  watch: {
    readonly(newValue) {
      this.editable = newValue
    },
  },

  methods: {
    handleEdit() {
      this.editable = false
    },
    handleSave() {
      const content = {
        typeId: this.content.typeId,
        content: this.updatedContent,
        lastUpdatedOn: new Date(),
        updatedById: this.user.userId,
      }

      this.$emit('onContentSave', content)
    },
    handleCancel() {
      this.editable = !this.editable
      this.updatedContent = this.content.content
      this.$emit('onContentChangeCancel', this.content)
    },
    handleUpdatedText(text) {
      if (!this.editable) {
        this.disableSave = text.replace(/\s/g, '') === this.emptyComment
        this.updatedContent = text
      }
    },
  },
}
</script>

<style lang="scss">
.asc-doc-mgmt-content-editor {
  &--card {
    margin: 16px 0;

    .asc-card__text {
      padding: 16px;

      .editor {
        &--header {
          margin-bottom: 21px;

          .editor-title {
            color: $primary-black;
            font-size: 14px;
            font-weight: 500;
            letter-spacing: 1px;
            line-height: 18px;
            text-transform: uppercase;
          }

          .editor-action {
            .asc-accent-button-container {
              margin-left: 8px;
            }
          }
        }

        &--content {
          margin-bottom: 7.63px;

          .editor {
            .editor__content.readonly {
              cursor: not-allowed;
            }
          }
        }

        &--footer {
          color: $gray-700;
          font-size: 12px;
          letter-spacing: 0;
          line-height: 18px;
          margin-bottom: 8px;
        }
      }
    }

    .asc-textarea-input--container .theme--light.v-input.v-textarea textarea {
      line-height: 32px;
    }
  }
}
</style>
